import React, { useState, useEffect, useRef } from 'react';
import { api } from '../../api/apiClient';
import html2canvas from 'html2canvas';
import { 
  RefreshCw, Save, Download, Eye, LayoutTemplate, Image as ImageIcon, 
  Type, Sparkles, Shuffle, Wand2, UserMinus, Trash2, Plus, Search, 
  X, ChevronLeft, ChevronRight, Layers, Tag
} from 'lucide-react';
import { toast } from 'sonner';

interface CarouselSlide {
  type: string;
  image?: string;
  images?: string[];
  text: string;
  textSize: string;
  brandId?: number;
  brandName?: string;
}

interface CarouselState {
  id: string;
  slides: CarouselSlide[];
  createdAt: string;
  frenchOnly?: boolean;
}

interface AvailableBrand {
  id: number;
  name: string;
  logo_url?: string;
  last_carousel_featured_at?: string | null;
  created_at?: string;
}

export const CarouselAdmin: React.FC = () => {
  const [carousel, setCarousel] = useState<CarouselState | null>(null);
  const [frenchOnly, setFrenchOnly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isAddingBrand, setIsAddingBrand] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editText, setEditText] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [loadingSlideAction, setLoadingSlideAction] = useState<number | null>(null);
  
  // Modal de sélection de marque
  const [showBrandPicker, setShowBrandPicker] = useState(false);
  const [availableBrands, setAvailableBrands] = useState<AvailableBrand[]>([]);
  const [brandSearchQuery, setBrandSearchQuery] = useState('');
  const [loadingBrandsList, setLoadingBrandsList] = useState(false);

  const exportContainerRef = useRef<HTMLDivElement>(null);

  const fetchCurrentCarousel = async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/carousel/current');
      if (res.data.success && res.data.carousel) {
        setCarousel(res.data.carousel);
        if (res.data.carousel.frenchOnly !== undefined) {
          setFrenchOnly(res.data.carousel.frenchOnly);
        }
      }
    } catch (err) {
      console.error('Erreur chargement carrousel:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCurrentCarousel();
  }, []);

  const generateNewCarousel = async () => {
    try {
      setIsGenerating(true);
      toast.info('Génération de 5 marques en cours avec l\'IA...', { duration: 5000 });
      const res = await api.post('/admin/carousel/generate', { frenchOnly });
      if (res.data.success) {
        setCarousel(res.data.carousel);
        const brandCount = res.data.carousel.slides.filter((s: any) => s.brandId).length;
        toast.success(`Carrousel généré avec succès (${brandCount} marques) !`);
      }
    } catch (err) {
      console.error('Erreur génération carrousel:', err);
      toast.error('Erreur lors de la génération du carrousel');
    } finally {
      setIsGenerating(false);
    }
  };

  const publishCarousel = async () => {
    try {
      await api.post('/admin/carousel/publish', {});
      toast.success('Carrousel publié ! Les marques ont été marquées comme "mises en avant".');
    } catch (err) {
      toast.error('Erreur lors de la publication.');
    }
  };

  const handleEditSlide = (index: number) => {
    setEditingIndex(index);
    setEditText(carousel?.slides[index].text || '');
  };

  const handleSaveEdit = async () => {
    if (editingIndex === null || !carousel) return;
    
    try {
      const res = await api.put(`/admin/carousel/slide/${editingIndex}`, { text: editText });
      if (res.data.success) {
        setCarousel(res.data.carousel);
        setEditingIndex(null);
        toast.success('Slide mise à jour !');
      }
    } catch (err: any) {
      const msg = err.response?.data?.error || 'Erreur lors de la mise à jour de la slide.';
      toast.error(msg);
      console.error('Save error:', err.response?.status, err.response?.data);
    }
  };

  const handleRegenerateText = async (index: number) => {
    try {
      setLoadingSlideAction(index);
      const res = await api.post(`/admin/carousel/slide/${index}/regenerate-text`, {});
      if (res.data.success) {
        setCarousel(res.data.carousel);
        toast.success('Texte regénéré avec succès !');
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Erreur lors de la regénération du texte.');
    } finally {
      setLoadingSlideAction(null);
    }
  };

  const handleRerollImages = async (index: number) => {
    try {
      setLoadingSlideAction(index);
      const res = await api.post(`/admin/carousel/slide/${index}/reroll-images`, {});
      if (res.data.success) {
        setCarousel(res.data.carousel);
        toast.success('Images re-tirées avec succès !');
      }
    } catch (err) {
      toast.error('Erreur lors du tirage des images.');
    } finally {
      setLoadingSlideAction(null);
    }
  };

  const handleReplaceBrand = async (index: number) => {
    try {
      setLoadingSlideAction(index);
      const res = await api.post(`/admin/carousel/slide/${index}/replace-brand`, {});
      if (res.data.success) {
        setCarousel(res.data.carousel);
        toast.success('Marque remplacée avec succès !');
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Erreur lors du remplacement de la marque.');
    } finally {
      setLoadingSlideAction(null);
    }
  };

  const handleDeleteSlide = async (index: number) => {
    if (!carousel) return;
    const slide = carousel.slides[index];
    if (slide.type === 'cover' || slide.type === 'cta') {
      toast.error('Impossible de supprimer cette slide.');
      return;
    }
    
    try {
      setLoadingSlideAction(index);
      const res = await api.delete(`/admin/carousel/slide/${index}`);
      if (res.data.success) {
        setCarousel(res.data.carousel);
        toast.success('Slide supprimée !');
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Erreur lors de la suppression.');
    } finally {
      setLoadingSlideAction(null);
    }
  };

  const handleMoveSlide = async (fromIndex: number, toIndex: number) => {
    if (!carousel) return;
    try {
      setLoadingSlideAction(fromIndex);
      const res = await api.post('/admin/carousel/slide/move', { fromIndex, toIndex });
      if (res.data.success) {
        setCarousel(res.data.carousel);
        toast.success('Ordre des slides mis à jour !');
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Impossible de déplacer la slide.');
    } finally {
      setLoadingSlideAction(null);
    }
  };

  // Ajouter une marque (aléatoire ou spécifique)
  const handleAddBrand = async (brandId?: number) => {
    try {
      setIsAddingBrand(true);
      const res = await api.post('/admin/carousel/add-brand', { brandId });
      if (res.data.success) {
        setCarousel(res.data.carousel);
        setShowBrandPicker(false);
        const brandName = res.data.addedBrand || 'Nouvelle marque';
        toast.success(`✨ ${brandName} ajoutée au carrousel !`);
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Erreur lors de l\'ajout de la marque.');
    } finally {
      setIsAddingBrand(false);
    }
  };

  // Charger les marques disponibles pour le modal
  const openBrandPicker = async () => {
    setShowBrandPicker(true);
    setBrandSearchQuery('');
    try {
      setLoadingBrandsList(true);
      const res = await api.get('/admin/carousel/available-brands');
      if (res.data.success && res.data.brands) {
        setAvailableBrands(res.data.brands);
      }
    } catch (err) {
      console.error('Erreur chargement marques disponibles:', err);
      toast.error('Erreur lors du chargement des marques.');
    } finally {
      setLoadingBrandsList(false);
    }
  };

  const filteredAvailableBrands = availableBrands.filter(b => 
    b.name.toLowerCase().includes(brandSearchQuery.toLowerCase().trim())
  );

  const exportSlides = async () => {
    if (!carousel || !exportContainerRef.current) return;
    
    setIsExporting(true);
    const toastId = toast.loading('Démarrage de l\'exportation...');

    try {
      const slidesElements = exportContainerRef.current.children;
      for (let i = 0; i < slidesElements.length; i++) {
        toast.loading(`Slide ${i + 1}/${slidesElements.length} : rendu et compression sans perte...`, { id: toastId });
        const element = slidesElements[i] as HTMLElement;
        const canvas = await html2canvas(element, {
          scale: 1, // Format officiel HD 1080x1920 Instagram / TikTok
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#000000',
        });
        
        // Convertir le canvas en Blob PNG
        const rawBlob = await new Promise<Blob | null>((resolve) => {
          canvas.toBlob((b) => resolve(b), 'image/png');
        });

        if (!rawBlob) {
          throw new Error(`Impossible de générer le blob pour la slide ${i + 1}`);
        }

        let finalBlob: Blob = rawBlob;

        // Compression lossless via sharp sur le serveur
        try {
          const formData = new FormData();
          formData.append('image', rawBlob, `slide-${i + 1}.png`);

          const compressRes = await api.post('/admin/carousel/compress', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            responseType: 'blob',
          });

          if (compressRes.data && compressRes.data.size > 0) {
            finalBlob = compressRes.data;
          }
        } catch (compressErr) {
          console.warn(`Compression backend échouée pour la slide ${i + 1}, utilisation du rendu brut`, compressErr);
        }

        const objectUrl = URL.createObjectURL(finalBlob);
        const link = document.createElement('a');
        link.download = `carousel-slide-${i + 1}.png`;
        link.href = objectUrl;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
        
        await new Promise(resolve => setTimeout(resolve, 400));
      }
      toast.success(`Exportation terminée (${slidesElements.length} slides compressées lossless) !`, { id: toastId });
    } catch (err) {
      console.error('Erreur lors de l\'exportation:', err);
      toast.error('Erreur lors de l\'exportation des images.', { id: toastId });
    } finally {
      setIsExporting(false);
    }
  };

  // Nombre de marques réelles dans le carrousel
  const brandSlidesCount = carousel?.slides.filter(s => s.brandId).length || 0;

  // Helper pour render une slide en preview TikTok
  const renderSlidePreview = (slide: CarouselSlide, index: number) => {
    const isCover = slide.type === 'cover';
    const isCTA = slide.type === 'cta';
    const isBrand = !isCover && !isCTA;

    // Calculer les bornes de déplacement pour les slides de marques
    const firstBrandIndex = carousel?.slides.findIndex(s => !s.type.includes('cover') && !s.type.includes('cta')) ?? 1;
    const lastBrandIndex = (carousel?.slides.length || 2) - 2; // avant le CTA
    const canMoveLeft = isBrand && index > firstBrandIndex;
    const canMoveRight = isBrand && index < lastBrandIndex;

    return (
      <div key={index} className="flex flex-col bg-black rounded-xl overflow-hidden border border-zinc-800 relative w-[300px] h-[533px] flex-shrink-0 group shadow-lg">
        {/* Images Background */}
        {isCover && slide.image && (
          <img src={slide.image} className="absolute inset-0 w-full h-full object-cover opacity-70" alt="Cover" />
        )}
        {isBrand && slide.images && slide.images.length > 0 && (
          <div className="absolute inset-0 w-full h-full grid grid-cols-2 grid-rows-2 gap-[2px] bg-black">
             {slide.images[0] && <div className="bg-zinc-900 w-full h-full" style={{ backgroundImage: `url(${slide.images[0]})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }}></div>}
             {slide.images[1] && <div className="bg-zinc-900 w-full h-full" style={{ backgroundImage: `url(${slide.images[1]})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }}></div>}
             {slide.images[2] && <div className="bg-zinc-900 w-full h-full" style={{ backgroundImage: `url(${slide.images[2]})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }}></div>}
             {slide.images[3] && <div className="bg-zinc-900 w-full h-full" style={{ backgroundImage: `url(${slide.images[3]})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }}></div>}
          </div>
        )}
        {isCTA ? (
          <div className="absolute inset-0 w-full h-full pointer-events-none">
            {slide.image && (
              <img src={slide.image} className="absolute inset-0 w-full h-full object-cover opacity-50" alt="CTA Background" />
            )}
            <div className="absolute inset-0 bg-black/60" />
            
            <div className="absolute bottom-48 left-0 right-0 flex flex-col items-center gap-3 px-6">
              <div className="flex gap-2 flex-wrap justify-center">
                <span className="px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-full text-[12px] text-white/90 font-medium border border-white/20 shadow-sm">Alertes drops</span>
                <span className="px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-full text-[12px] text-white/90 font-medium border border-white/20 shadow-sm">Swipe marques indé</span>
              </div>
              <div className="flex gap-2 flex-wrap justify-center">
                <span className="px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-full text-[12px] text-white/90 font-medium border border-white/20 shadow-sm">Tes collections</span>
                <span className="px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-full text-[12px] text-white/90 font-medium border border-white/20 shadow-sm">Catalogues en temps réel</span>
              </div>
            </div>

            <div className="absolute bottom-32 left-0 right-0 flex justify-center">
              <span className="text-[14px] font-bold text-white/60 tracking-[0.15em] uppercase drop-shadow-md">pepitespot.fr</span>
            </div>
          </div>
        ) : null}

        {/* Overlay TikTok UI Mockup */}
        <div className="absolute top-4 right-4 flex flex-col gap-4 items-center opacity-50 pointer-events-none">
          <div className="w-10 h-10 rounded-full bg-white/20" />
          <div className="w-8 h-8 rounded-full bg-white/20" />
          <div className="w-8 h-8 rounded-full bg-white/20" />
          <div className="w-8 h-8 rounded-full bg-white/20" />
        </div>
        <div className="absolute bottom-4 left-4 right-4 h-16 bg-gradient-to-t from-black/80 to-transparent pointer-events-none" />

        {/* Text Overlay */}
        <div className={`absolute inset-0 flex items-center justify-center p-6 ${isCTA ? 'pb-60' : 'pb-24'} text-center z-10`}>
           {editingIndex === index ? (
             <div className="flex flex-col gap-2 w-full">
                <textarea 
                  className="w-full bg-black/80 text-white p-3 rounded-lg border border-zinc-700 outline-none text-xl text-center"
                  rows={4}
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                />
                <div className="flex justify-end gap-2">
                  <button onClick={() => setEditingIndex(null)} className="px-3 py-1 bg-gray-700 text-white rounded text-sm hover:bg-gray-600 transition-colors">Annuler</button>
                  <button onClick={handleSaveEdit} className="px-3 py-1 bg-white text-black rounded text-sm font-semibold hover:bg-gray-200 transition-colors">Sauvegarder</button>
                </div>
             </div>
           ) : (
              <h2 
                className="font-bold tracking-tight leading-tight relative cursor-pointer hover:scale-105 transition-transform"
                style={{ 
                   fontSize: Math.max(parseInt(slide.textSize) / 1.5, 20) + 'px',
                   color: 'white',
                   textShadow: '0px 3px 8px rgba(0,0,0,0.9), 0px 0px 4px rgba(0,0,0,0.9), 0px 1px 2px rgba(0,0,0,1)',
                   whiteSpace: 'pre-wrap',
                   textTransform: 'none'
                }}
                onClick={() => handleEditSlide(index)}
                title="Cliquez pour modifier le texte"
              >
                 {slide.text}
              </h2>
           )}
        </div>
        
        {/* Action Buttons Overlay */}
        <div className="absolute top-2 left-2 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity z-20">
            <button 
                onClick={(e) => { e.stopPropagation(); handleEditSlide(index); }}
                className="bg-black/80 text-white p-2 rounded-lg hover:bg-zinc-800 transition-colors shadow-md"
                title="Modifier le texte manuellement"
            >
                <Type size={16} />
            </button>
            <button 
                onClick={(e) => { e.stopPropagation(); handleRegenerateText(index); }}
                disabled={loadingSlideAction === index}
                className="bg-black/80 text-amber-400 p-2 rounded-lg hover:bg-zinc-800 transition-colors disabled:opacity-50 shadow-md"
                title="Regénérer le texte avec l'IA"
            >
                <Wand2 size={16} className={loadingSlideAction === index ? "animate-spin" : ""} />
            </button>
            {isBrand && (
              <>
                <button 
                    onClick={(e) => { e.stopPropagation(); handleRerollImages(index); }}
                    disabled={loadingSlideAction === index}
                    className="bg-black/80 text-emerald-400 p-2 rounded-lg hover:bg-zinc-800 transition-colors disabled:opacity-50 shadow-md"
                    title="Changer les images (aléatoire)"
                >
                    <Shuffle size={16} className={loadingSlideAction === index ? "animate-spin" : ""} />
                </button>
                <button 
                    onClick={(e) => { e.stopPropagation(); handleReplaceBrand(index); }}
                    disabled={loadingSlideAction === index}
                    className="bg-black/80 text-amber-400 p-2 rounded-lg hover:bg-zinc-800 transition-colors disabled:opacity-50 shadow-md"
                    title="Remplacer la marque par une autre"
                >
                    <UserMinus size={16} className={loadingSlideAction === index ? "animate-spin" : ""} />
                </button>
                {canMoveLeft && (
                  <button 
                      onClick={(e) => { e.stopPropagation(); handleMoveSlide(index, index - 1); }}
                      disabled={loadingSlideAction === index}
                      className="bg-black/80 text-amber-400 p-2 rounded-lg hover:bg-zinc-800 transition-colors disabled:opacity-50 shadow-md"
                      title="Déplacer vers la gauche"
                  >
                      <ChevronLeft size={16} />
                  </button>
                )}
                {canMoveRight && (
                  <button 
                      onClick={(e) => { e.stopPropagation(); handleMoveSlide(index, index + 1); }}
                      disabled={loadingSlideAction === index}
                      className="bg-black/80 text-amber-400 p-2 rounded-lg hover:bg-zinc-800 transition-colors disabled:opacity-50 shadow-md"
                      title="Déplacer vers la droite"
                  >
                      <ChevronRight size={16} />
                  </button>
                )}
                <button 
                    onClick={(e) => { e.stopPropagation(); handleDeleteSlide(index); }}
                    disabled={loadingSlideAction === index}
                    className="bg-black/80 text-red-500 p-2 rounded-lg hover:bg-red-950 transition-colors disabled:opacity-50 shadow-md"
                    title="Supprimer cette slide du carrousel"
                >
                    <Trash2 size={16} className={loadingSlideAction === index ? "animate-spin" : ""} />
                </button>
              </>
            )}
        </div>
      </div>
    );
  };

  // Helper pour render la slide en pleine résolution (off-screen pour export)
  const renderSlideFullRes = (slide: CarouselSlide, index: number) => {
    const isCover = slide.type === 'cover';
    const isCTA = slide.type === 'cta';
    const isBrand = !isCover && !isCTA;

    return (
      <div key={`export-${index}`} className="flex flex-col bg-black overflow-hidden relative w-[1080px] h-[1920px] flex-shrink-0">
        {/* Images Background */}
        {isCover && slide.image && (
          <img src={slide.image} className="absolute inset-0 w-full h-full object-cover opacity-70" crossOrigin="anonymous" alt="Cover" />
        )}
        {isBrand && slide.images && slide.images.length > 0 && (
          <div className="absolute inset-0 w-full h-full grid grid-cols-2 grid-rows-2 gap-4 bg-black">
             {slide.images[0] && <div className="bg-zinc-900 w-full h-full" style={{ backgroundImage: `url(${slide.images[0]})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }}></div>}
             {slide.images[1] && <div className="bg-zinc-900 w-full h-full" style={{ backgroundImage: `url(${slide.images[1]})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }}></div>}
             {slide.images[2] && <div className="bg-zinc-900 w-full h-full" style={{ backgroundImage: `url(${slide.images[2]})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }}></div>}
             {slide.images[3] && <div className="bg-zinc-900 w-full h-full" style={{ backgroundImage: `url(${slide.images[3]})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }}></div>}
          </div>
        )}
        {isCTA ? (
          <div className="absolute inset-0 w-full h-full">
            {slide.image && (
              <img src={slide.image} className="absolute inset-0 w-full h-full object-cover opacity-50" crossOrigin="anonymous" alt="CTA Background" />
            )}
            <div className="absolute inset-0 bg-black/60" />
            
            <div className="absolute bottom-[450px] left-0 right-0 flex flex-col items-center gap-8 px-16">
              <div className="flex gap-6 flex-wrap justify-center">
                <span className="px-8 py-3 bg-white/10 backdrop-blur-sm rounded-full text-[28px] text-white/90 font-medium border border-white/20 shadow-md">Alertes drops</span>
                <span className="px-8 py-3 bg-white/10 backdrop-blur-sm rounded-full text-[28px] text-white/90 font-medium border border-white/20 shadow-md">Swipe marques indé</span>
              </div>
              <div className="flex gap-6 flex-wrap justify-center">
                <span className="px-8 py-3 bg-white/10 backdrop-blur-sm rounded-full text-[28px] text-white/90 font-medium border border-white/20 shadow-md">Tes collections</span>
                <span className="px-8 py-3 bg-white/10 backdrop-blur-sm rounded-full text-[28px] text-white/90 font-medium border border-white/20 shadow-md">Catalogues en temps réel</span>
              </div>
            </div>

            <div className="absolute bottom-[350px] left-0 right-0 flex justify-center">
              <span className="text-[28px] font-bold text-white/60 tracking-[0.15em] uppercase drop-shadow-md">pepitespot.fr</span>
            </div>
          </div>
        ) : null}

        {/* Text Overlay */}
        <div className={`absolute inset-0 flex items-center justify-center p-16 ${isCTA ? 'pb-[650px]' : 'pb-[300px]'} text-center z-10`}>
           <h2 
              className="font-bold tracking-tight leading-tight relative"
              style={{ 
                 fontSize: Math.max(parseInt(slide.textSize) * 2, 60) + 'px',
                 color: 'white',
                 textShadow: '0px 6px 16px rgba(0,0,0,0.9), 0px 0px 8px rgba(0,0,0,0.9), 0px 2px 4px rgba(0,0,0,1)',
                 whiteSpace: 'pre-wrap',
                 textTransform: 'none'
              }}
            >
              {slide.text}
           </h2>
        </div>
      </div>
    );
  };

  return (
    <div className="p-8 max-w-[1400px] mx-auto text-white">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <LayoutTemplate className="text-emerald-500" />
            Studio Carrousel TikTok
          </h1>
          <p className="text-gray-400 mt-2">Générez des carrousels "Pépite" prêts à poster avec IA (5 marques garanties par défaut).</p>
        </div>
        <div className="flex flex-wrap gap-3 items-center">
          <label className="flex items-center gap-2 cursor-pointer bg-zinc-800 border border-zinc-700 px-4 py-3 rounded-lg hover:bg-zinc-700 transition-colors">
            <input 
              type="checkbox" 
              checked={frenchOnly}
              onChange={(e) => setFrenchOnly(e.target.checked)}
              className="w-5 h-5 accent-emerald-500 rounded bg-zinc-900 border-zinc-700"
            />
            <span className="font-semibold text-white">🇫🇷 100% FR</span>
          </label>
          
          <button 
            onClick={generateNewCarousel}
            disabled={isGenerating}
            className="flex items-center gap-2 px-6 py-3 bg-white text-black font-bold rounded-lg hover:bg-gray-200 transition-all shadow-[0_0_20px_rgba(255,255,255,0.2)] hover:scale-105 disabled:opacity-50 disabled:hover:scale-100"
          >
            {isGenerating ? <RefreshCw className="animate-spin" size={20} /> : <Sparkles size={20} />}
            {isGenerating ? 'Génération...' : 'Nouveau Carrousel (5 Marques)'}
          </button>

          {carousel && (
            <>
              <button 
                onClick={() => handleAddBrand()}
                disabled={isAddingBrand}
                className="flex items-center gap-2 px-4 py-3 bg-emerald-600/20 text-emerald-400 border border-emerald-500/30 font-semibold rounded-lg hover:bg-emerald-600/30 transition-all disabled:opacity-50"
                title="Ajouter une marque aléatoire supplémentaire"
              >
                {isAddingBrand ? <RefreshCw className="animate-spin" size={18} /> : <Plus size={18} />}
                + Marque Aléatoire
              </button>

              <button 
                onClick={openBrandPicker}
                disabled={isAddingBrand}
                className="flex items-center gap-2 px-4 py-3 bg-zinc-800 text-zinc-300 border border-zinc-700 font-semibold rounded-lg hover:bg-zinc-700 transition-all disabled:opacity-50"
                title="Choisir une marque spécifique dans la liste"
              >
                <Search size={18} />
                Choisir Marque...
              </button>

              <button 
                onClick={publishCarousel}
                className="flex items-center gap-2 px-5 py-3 bg-zinc-800 text-white font-bold rounded-lg border border-zinc-700 hover:bg-zinc-700 transition-colors"
              >
                <Save size={20} />
                Marquer Publié
              </button>
            </>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64 text-zinc-500">
           <RefreshCw className="animate-spin mr-3" /> Chargement...
        </div>
      ) : !carousel ? (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-12 text-center flex flex-col items-center justify-center">
          <ImageIcon className="text-zinc-600 mb-4" size={48} />
          <h3 className="text-xl font-bold mb-2">Aucun carrousel généré</h3>
          <p className="text-zinc-500 mb-6 max-w-md">
            Cliquez sur "Nouveau Carrousel" pour que l'IA pioche 5 marques distinctes 
            et génère les visuels et textes d'accroche.
          </p>
          <button 
            onClick={generateNewCarousel}
            className="flex items-center gap-2 px-6 py-3 bg-white text-black font-bold rounded-lg hover:bg-gray-200 transition-all"
          >
            <Sparkles size={20} />
            Générer maintenant
          </button>
        </div>
      ) : (
        <div className="space-y-8">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-3">
              <div>
                <h3 className="text-lg font-semibold flex items-center gap-2">
                   <Eye className="text-zinc-400" size={18} />
                   Prévisualisation (Format 9:16)
                </h3>
                <div className="flex items-center gap-3 mt-1">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                    <Tag size={12} />
                    {brandSlidesCount} marques dans le carrousel
                  </span>
                  <span className="text-xs text-zinc-500">
                    Total : {carousel.slides.length} slides (Cover + Marques + CTA)
                  </span>
                </div>
              </div>
              <div className="text-sm text-zinc-500">
                 Généré le : {new Date(carousel.createdAt).toLocaleString('fr-FR')}
              </div>
            </div>
            
            {/* Horizontal Scroll for Slides */}
            <div className="flex overflow-x-auto gap-6 pb-6 snap-x hide-scrollbar items-start">
              {carousel.slides.map((slide, i) => {
                const isCTA = slide.type === 'cta';
                return (
                  <React.Fragment key={i}>
                    {/* Insérer la carte "+ Ajouter une marque" juste avant le CTA */}
                    {isCTA && (
                      <div className="snap-center flex flex-col justify-center items-center w-[260px] h-[533px] flex-shrink-0 bg-zinc-950/60 border-2 border-dashed border-zinc-800 rounded-xl p-6 hover:border-emerald-500/50 transition-all text-center group">
                        <div className="w-14 h-14 rounded-full bg-zinc-900 flex items-center justify-center text-zinc-400 group-hover:text-emerald-400 group-hover:scale-110 group-hover:bg-emerald-500/10 transition-all mb-4">
                          <Plus size={28} />
                        </div>
                        <h4 className="font-bold text-white mb-1">Ajouter une marque</h4>
                        <p className="text-xs text-zinc-500 mb-6">
                          Insérez une marque supplémentaire dans ce carrousel
                        </p>
                        <div className="flex flex-col gap-2.5 w-full">
                          <button
                            onClick={() => handleAddBrand()}
                            disabled={isAddingBrand}
                            className="w-full flex items-center justify-center gap-2 py-2.5 px-3 bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-400 border border-emerald-500/30 rounded-lg text-xs font-semibold transition-all disabled:opacity-50"
                          >
                            {isAddingBrand ? <RefreshCw className="animate-spin" size={14} /> : <Sparkles size={14} />}
                            Aléatoire (IA)
                          </button>
                          <button
                            onClick={openBrandPicker}
                            disabled={isAddingBrand}
                            className="w-full flex items-center justify-center gap-2 py-2.5 px-3 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 border border-zinc-700 rounded-lg text-xs font-semibold transition-all disabled:opacity-50"
                          >
                            <Search size={14} />
                            Choisir dans la liste
                          </button>
                        </div>
                      </div>
                    )}

                    <div className="snap-center">
                      {renderSlidePreview(slide, i)}
                      <div className="text-center text-zinc-400 text-sm mt-3 font-medium flex items-center justify-center gap-1.5">
                         {slide.type === 'cover' ? (
                           <span className="text-zinc-500 font-semibold">Slide {i + 1} • Couverture</span>
                         ) : slide.type === 'cta' ? (
                           <span className="text-zinc-500 font-semibold">Slide {i + 1} • Fin (CTA)</span>
                         ) : (
                           <span className="text-white font-bold bg-zinc-800 px-2 py-0.5 rounded text-xs">
                             Slide {i + 1} • {slide.brandName || `Marque ${slide.brandId}`}
                           </span>
                         )}
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
            
            <div className="mt-6 p-4 bg-zinc-800/50 rounded-lg border border-zinc-700/50 text-sm text-zinc-400 flex flex-wrap items-center justify-between gap-3">
              <div>
                <span className="text-white font-semibold">💡 Astuces :</span> Cliquez sur le texte d'une slide pour le modifier manuellement. Utilisez les boutons au survol de chaque carte pour changer les images, remplacer la marque, la déplacer (⬅️ / ➡️) ou la supprimer (🗑️).
              </div>
            </div>
          </div>
          
          {/* Section Téléchargement / Export */}
          <div className="bg-black border border-emerald-900/30 rounded-xl p-6 flex flex-col items-center justify-center text-center">
             <Download className="text-emerald-500 mb-3" size={32} />
             <h3 className="text-lg font-bold text-white mb-2">Exporter le rendu HD Lossless</h3>
             <p className="text-zinc-400 max-w-lg mb-6">
               Téléchargez toutes les {carousel.slides.length} slides en haute résolution (1080x1920) avec compression sans perte (lossless), prêtes à être postées sur TikTok ou Instagram.
             </p>
             <button 
               onClick={exportSlides}
               disabled={isExporting}
               className="flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white font-bold rounded-lg hover:bg-emerald-500 transition-all disabled:opacity-50 shadow-[0_0_20px_rgba(16,185,129,0.3)]"
             >
               {isExporting ? <RefreshCw className="animate-spin" size={20} /> : <Download size={20} />}
               {isExporting ? 'Compression & export...' : 'Télécharger toutes les Images (Lossless)'}
             </button>
          </div>

          {/* Off-screen container for rendering full resolution images */}
          <div className="overflow-hidden absolute top-[-9999px] left-[-9999px]">
            <div ref={exportContainerRef} className="flex flex-col gap-10">
               {carousel.slides.map((slide, i) => renderSlideFullRes(slide, i))}
            </div>
          </div>
        </div>
      )}

      {/* Modal de sélection de marque */}
      {showBrandPicker && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-xl max-h-[85vh] flex flex-col overflow-hidden shadow-2xl animate-in fade-in zoom-in-95 duration-200">
            {/* Modal Header */}
            <div className="p-5 border-b border-zinc-800 flex justify-between items-center">
              <div>
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                  <Tag className="text-emerald-500" size={20} />
                  Choisir une marque à ajouter
                </h3>
                <p className="text-xs text-zinc-400 mt-1">
                  Sélectionnez une marque du catalogue pour l'intégrer au carrousel
                </p>
              </div>
              <button 
                onClick={() => setShowBrandPicker(false)}
                className="p-2 text-zinc-400 hover:text-white rounded-lg hover:bg-zinc-800 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Search Bar */}
            <div className="p-4 border-b border-zinc-800 bg-zinc-950/40">
              <div className="relative">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
                <input 
                  type="text"
                  placeholder="Rechercher une marque (ex: Walk in Paris, Weyz...)"
                  value={brandSearchQuery}
                  onChange={(e) => setBrandSearchQuery(e.target.value)}
                  className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-xl pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-emerald-500 transition-colors"
                  autoFocus
                />
              </div>
            </div>

            {/* Modal Brand List */}
            <div className="p-4 overflow-y-auto flex-1 divide-y divide-zinc-800/60 max-h-[450px]">
              {loadingBrandsList ? (
                <div className="flex flex-col items-center justify-center py-12 text-zinc-500">
                  <RefreshCw className="animate-spin mb-2" size={24} />
                  <span>Chargement des marques éligibles...</span>
                </div>
              ) : filteredAvailableBrands.length === 0 ? (
                <div className="text-center py-12 text-zinc-500">
                  <p className="font-semibold">Aucune marque trouvée</p>
                  <p className="text-xs mt-1">Essayez un autre mot-clé ou vérifiez que toutes les marques ne sont pas déjà ajoutées.</p>
                </div>
              ) : (
                filteredAvailableBrands.map((b) => (
                  <div 
                    key={b.id}
                    className="flex items-center justify-between py-3 px-3 hover:bg-zinc-800/60 rounded-xl transition-colors group cursor-pointer"
                    onClick={() => !isAddingBrand && handleAddBrand(b.id)}
                  >
                    <div className="flex items-center gap-3">
                      {b.logo_url ? (
                        <img 
                          src={b.logo_url} 
                          alt={b.name} 
                          className="w-10 h-10 object-contain rounded-lg bg-zinc-950 p-1 border border-zinc-800"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-zinc-800 border border-zinc-700 flex items-center justify-center font-bold text-xs text-zinc-400">
                          {b.name.slice(0, 2).toUpperCase()}
                        </div>
                      )}
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-white group-hover:text-emerald-400 transition-colors">
                            {b.name}
                          </h4>
                          {b.created_at && (Date.now() - new Date(b.created_at).getTime() < 30 * 24 * 60 * 60 * 1000) && (
                            <span className="px-1.5 py-0.5 text-[10px] bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded font-medium">
                              Nouveau
                            </span>
                          )}
                        </div>
                        <span className="text-[11px] text-zinc-500">
                          {b.last_carousel_featured_at ? (
                            `Mis en avant le ${new Date(b.last_carousel_featured_at).toLocaleDateString('fr-FR')}`
                          ) : (
                            'Jamais mis en avant'
                          )}
                        </span>
                      </div>
                    </div>

                    <button
                      disabled={isAddingBrand}
                      className="px-3 py-1.5 bg-emerald-600/10 text-emerald-400 border border-emerald-500/20 group-hover:bg-emerald-600 group-hover:text-white rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5"
                    >
                      <Plus size={14} />
                      Ajouter
                    </button>
                  </div>
                ))
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-zinc-800 bg-zinc-950/40 flex justify-between items-center text-xs text-zinc-500">
              <span>{filteredAvailableBrands.length} marque(s) disponible(s)</span>
              <button 
                onClick={() => setShowBrandPicker(false)}
                className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg transition-colors font-medium"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

